<template>
  <JoszakiTooltip
    v-if="hasFeature(FEATURES.FAVOURITES)"
    :text="ariaLabel"
    class="inline-flex"
    :class="wrapperClass"
  >
    <button
      type="button"
      class="inline-flex items-center justify-center text-primary"
      :class="buttonClass"
      :aria-label="ariaLabel"
      @click.prevent.stop="onClick"
    >
      <IconComponent
        v-if="pending"
        class="animate-spin w-5 h-5"
        icon="spinner"
      />
      <IconComponent v-else-if="favourited" class="w-5 h-5" icon="bookmark" />
      <IconComponent v-else pack="far" icon="bookmark" class="w-5 h-5" />
    </button>
  </JoszakiTooltip>
</template>

<script>
import { computed, ref, useContext, useRoute } from "@nuxtjs/composition-api";
import { useFavourites } from "~/composables/favourite/useFavourites";
import useFeatures, { FEATURES } from "~/composables/useFeatures";
import JoszakiTooltip from "~/components/_refactored/common/JoszakiTooltip.vue";

export default {
  name: "FavouriteButton",
  components: { JoszakiTooltip },
  props: {
    entityType: {
      type: String,
      required: true,
    },
    entityId: {
      type: [String, Number],
      required: true,
    },
    buttonClass: {
      type: [String, Array, Object],
      default: "",
    },
    /**
     * Classes for the tooltip wrapper (the popper anchor). Put positioning here
     * — e.g. `absolute top-3 right-3` — so the tooltip anchors to the button's
     * actual on-screen location rather than its in-flow position.
     */
    wrapperClass: {
      type: [String, Array, Object],
      default: "",
    },
  },
  setup(props, { emit }) {
    const { i18n, $trackers, $helpers } = useContext();
    const route = useRoute();
    const { isFavourite, toggle } = useFavourites();
    const { hasFeature } = useFeatures();
    const pending = ref(false);

    // The favourite stores the URL of the page where the favouriting happened,
    // i.e. the page this button is rendered on.
    const currentUrl = computed(
      () => `${$helpers.topDomain}${route.value.fullPath}`
    );

    const favourited = computed(() =>
      isFavourite(props.entityType, props.entityId)
    );

    const ariaLabel = computed(() =>
      favourited.value
        ? i18n.t("favourites.removeAria")
        : i18n.t("favourites.addAria")
    );

    async function onClick() {
      if (pending.value) {
        return;
      }
      // Marking (not un-marking) is reported to PostHog as "Marked as Favourite".
      if (!favourited.value) {
        $trackers.trackEvent({
          title: "Marked as Favourite",
          data: {
            entityType: props.entityType,
            entityId: props.entityId,
          },
        });
      }
      pending.value = true;
      try {
        const result = await toggle(props.entityType, props.entityId, {
          url: currentUrl.value,
        });
        if (result !== null) {
          emit("change", {
            entityType: props.entityType,
            entityId: props.entityId,
            favourited: result,
          });
        }
      } finally {
        pending.value = false;
      }
    }

    return {
      pending,
      favourited,
      ariaLabel,
      onClick,
      hasFeature,
      FEATURES,
    };
  },
};
</script>
